import {
  Button,
  ButtonType,
  ConfirmationModal,
  FlexProps,
  FormDropdown,
  Hr,
} from '@access-erp/ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ProductTypeWithId } from '@/utils/data-helpers/types';
import { validationSchema } from './validationSchema';
import useUpdateProductBillingType from '../../hooks/useUpdateProductBillingType';
import { PreloadData } from '@/utils/microfrontend/preloadData';
import styles from './billing-modal.module.scss';

export type ProductOption = { label: string; value: string };

interface ModalProps {
  onClose: () => void;
  isVisible: boolean;
  selectedRow: ProductTypeWithId;
  refreshGrid: () => void;
}

export function BillingTypeModal({ onClose, isVisible, selectedRow, refreshGrid }: ModalProps) {
  const methods = useForm({
    defaultValues: {
      billingType: selectedRow.defaultBillingType,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    context: selectedRow,
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { updateBillingType } = useUpdateProductBillingType();
  const billingTypes = useMemo(() => PreloadData.billingTypes(), []);
  const billingTypesMapped = billingTypes.map((type) => ({
    value: type.type,
    label: type.formattedType,
  }));

  const currentBillingType = selectedRow.defaultBillingType
    ? {
      value: selectedRow.defaultBillingType,
      label: selectedRow.defaultBillingType,
    }
    : undefined;

  const handleSave = async () => {
    setShowConfirmationModal(true);
  };

  const handleGridRefresh = () => {
    setShowConfirmationModal(false);
    onClose();
    refreshGrid();
  };
  const handleConfirm = () => {
    updateBillingType(
      {
        input: {
          productId: selectedRow.iD,
          defaultBillingType: methods.getValues().billingType,
        },
      },
      handleGridRefresh,
    );
  };

  return (
        <>
            <ConfirmationModal
                open={isVisible}
                onClose={onClose}
                title="Change Billing Type"
                footerAlign={FlexProps['space-between']}
                hideFooter={true}
                width="small"
                id="confirmationModal"
            >
                <FormProvider {...methods}>
                    <div>
                        <div className={styles['dropdown-margin']}>
                            <FormDropdown<ProductOption>
                                label="Billing Type"
                                name="billingType"
                                id="billingType"
                                placeholder="Select billing type"
                                isClearable={false}
                                onChange={(newValue: string) => {
                                  methods.setValue('billingType', newValue, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }}
                                options={billingTypesMapped}
                                getOptionLabel={(option: ProductOption) => option?.label}
                                getOptionValue={(option: ProductOption) => option?.value}
                                defaultValue={currentBillingType}
                                required={true}
                            />
                        </div>
                    </div>
                </FormProvider>

                <div style={{ margin: '20px 0' }}>
                    <Hr />
                </div>
                <div className={styles['modal-buttons']}>
                    <Button
                        wrapperStyle={{ width: 'initial', flex: 'none' }}
                        buttonType={ButtonType.Secondary}
                        text="Cancel"
                        onClick={onClose}
                    />
                    <Button
                        wrapperStyle={{ width: 'initial', flex: 'none' }}
                        buttonType={ButtonType.Primary}
                        text="Save"
                        onClick={handleSave}
                        disabled={!methods.formState.isDirty || !methods.formState.isValid}
                    />
                </div>
            </ConfirmationModal>

            <ConfirmationModal
                open={showConfirmationModal}
                title="Changing Billing Type"
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirm}
            >
                You`re about to change the product billing type. Please confirm you wish to continue.
            </ConfirmationModal>
        </>
  );
}
